<script setup lang="ts">
import { useCurrentAccountManager } from '@/api/queries/accountManagers';
import { POPUPS } from '@/components/dynamic/maps';
import type { PaywallSurface } from '~/constants/paywallSurfaces';

// The paywall surface for a locked feature — "sees everything, accesses
// nothing". A blurred teaser of the real thing (default slot) or a decorative
// product mock sits under a lock card that drives the three upgrade paths from
// the package doc: Subscribe, message your Account Manager, or book a meeting.
// Drop-in: all actions are handled internally, so a caller only supplies the
// copy and (optionally) the content to blur behind it.
const { t } = useI18n();

const {
  requiredTier = 'PRO',
  requiredTierLabel,
  title,
  surface,
  teaser = '',
  points = [],
} = defineProps<{
  requiredTier?: 'MEMBER' | 'PRO';
  requiredTierLabel?: string;
  title: string;
  /** Stable English id for analytics + the AM prefill (the title is translated). */
  surface: PaywallSurface;
  teaser?: string;
  points?: string[];
}>();

// defineProps defaults can't reference setup locals (t), so the translated
// fallback lives in a computed.
const tierLabel = computed(() => requiredTierLabel ?? t('billing.plans.pro'));

const { openModal } = useBillingModal();
const { isUae, nextStepCopy } = useUaeGate();
const { data: accountManager } = useCurrentAccountManager();
const { sendEventToBackend } = useTracking();
const uiStore = useUIStore();

// The lock only mounts when a feature is gated, so treat it as a paywall impression.
onMounted(() => {
  void sendEventToBackend('paywall_viewed', { tier: requiredTier, surface });
});

const DEFAULT_WHATSAPP = '971527033506';
const amMessageLabel = computed(() =>
  accountManager.value?.firstName
    ? t('billing.curiosityLock.messageAm', { name: accountManager.value.firstName })
    : t('billing.curiosityLock.messageAmFallback'),
);
// Prefill goes to the account manager / ThriveState team (internal ops) — kept in English on purpose.
const whatsAppLink = computed(() => {
  const rawPhone = accountManager.value?.phone;
  const phone = rawPhone ? rawPhone.replace(/\D/g, '') : DEFAULT_WHATSAPP;
  const name = accountManager.value?.firstName || 'the team';
  const text = encodeURIComponent(`Hi ${name}, I'd like to unlock ${surface} on Insiders.`);
  return `https://wa.me/${phone}?text=${text}`;
});

// Decorative "product behind frosted glass" mock, shown when no real content is
// slotted behind the lock.
const chartBars = ['46%', '68%', '54%', '82%', '61%', '90%', '73%', '85%'];
const listRows = ['84%', '62%', '92%', '55%'];

// Open the pricing popup on the plans grid — same as the nav "Go Pro" — rather
// than deep-linking straight into a plan's checkout (which skips the plan
// comparison and, until Member billing is configured, lands on a broken step).
// Picking a plan in the grid is what fires `checkout_started`.
const onSubscribe = () => openModal();
const onMessageAm = () => {
  if (import.meta.client) window.open(whatsAppLink.value, '_blank', 'noopener,noreferrer');
};
// Open the booking popup directly (inline calendar → confirmation) rather than
// routing to the whole /appointments management page first. "My Appointments"
// stays reachable from the menu for viewing/rescheduling.
const onBookMeeting = () => uiStore.showPopup(POPUPS.BOOK_A_CALL, { standaloneBooking: true });
</script>

<template>
  <div class="cl">
    <!-- Teaser layer: real content (slot) or a decorative product mock, behind
         light frosted glass. -->
    <div
      class="cl-glass"
      aria-hidden="true"
    >
      <div class="cl-glass-inner">
        <slot v-if="$slots.default" />
        <div
          v-else
          class="cl-mock"
        >
          <div class="cl-mock-head">
            <span class="cl-mock-avatar" />
            <span
              class="cl-mock-bar"
              style="width: 34%"
            />
            <span class="cl-mock-chip" />
          </div>
          <div class="cl-mock-stats">
            <span
              v-for="n in 3"
              :key="n"
              class="cl-mock-stat"
            />
          </div>
          <div class="cl-mock-chart">
            <span
              v-for="(h, i) in chartBars"
              :key="i"
              class="cl-mock-cbar"
              :style="{ height: h, animationDelay: `${i * 90}ms` }"
            />
          </div>
          <div class="cl-mock-list">
            <span
              v-for="(w, i) in listRows"
              :key="i"
              class="cl-mock-row"
              :style="{ width: w }"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Scrim so the teaser reads as "just out of reach" -->
    <div
      class="cl-scrim"
      aria-hidden="true"
    />

    <!-- Lock card -->
    <div class="cl-card">
      <span
        class="mb-4 inline-flex items-center gap-1.5 rounded-full border border-gold-primary-100/50 bg-gold-primary-30/50 px-3 py-1.5"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-gold-primary-120"
          aria-hidden="true"
        >
          <rect
            x="3"
            y="11"
            width="18"
            height="11"
            rx="2"
          />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        <span class="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-gold-primary-120">
          {{
            isUae ? t('billing.uaeGate.nextStepBadge') : t('billing.curiosityLock.featureBadge', { tier: tierLabel })
          }}
        </span>
      </span>

      <h3
        class="max-w-[26ch] font-serif text-[26px] font-semibold leading-[1.08] tracking-[-0.02em] text-black-100 [text-wrap:balance] tablet:text-[30px]"
      >
        {{ title }}
      </h3>
      <p
        v-if="teaser"
        class="mt-2.5 max-w-[46ch] text-[14px] leading-[1.5] text-black-60 [text-wrap:pretty] tablet:text-[15px]"
      >
        {{ teaser }}
      </p>

      <ul
        v-if="points.length"
        class="mt-5 grid gap-2 text-start"
      >
        <li
          v-for="point in points"
          :key="point"
          class="flex items-start gap-2.5 text-[13.5px] leading-[1.45] text-black-70"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="mt-[2px] shrink-0 text-gold-primary-120"
            aria-hidden="true"
          >
            <path d="M12 2l2.09 6.26L20 9.27l-4.5 3.9L17 20l-5-3.2L7 20l1.5-6.83L4 9.27l5.91-1.01L12 2z" />
          </svg>
          <span>{{ point }}</span>
        </li>
      </ul>

      <!-- Upgrade paths: Subscribe / Message AM / Book a meeting -->
      <div class="mt-7 flex w-full max-w-[400px] flex-col gap-2.5">
        <button
          type="button"
          class="cl-cta-primary inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gold-primary-100 px-6 text-[15px] font-semibold text-black-100 shadow-[0_10px_24px_rgba(255,182,72,0.32)] transition-transform hover:-translate-y-0.5 hover:bg-gold-primary-120"
          @click="onSubscribe"
        >
          <span class="cl-cta-label">{{
            isUae ? t(nextStepCopy.ctaKey) : t('billing.curiosityLock.unlockWith', { tier: tierLabel })
          }}</span>
          <span
            class="cl-cta-arrow"
            aria-hidden="true"
            >&rarr;</span
          >
        </button>
        <div class="flex flex-col gap-2.5 sm:flex-row">
          <button
            type="button"
            class="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-black-10 bg-white px-4 text-[13.5px] font-semibold text-black-100 transition-colors hover:bg-black-03"
            @click="onMessageAm"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 32 32"
              fill="#25D366"
              aria-hidden="true"
            >
              <path
                d="M16 2C8.28 2 2 8.28 2 16c0 2.48.65 4.8 1.79 6.82L2 30l7.36-1.76A13.9 13.9 0 0 0 16 30c7.72 0 14-6.28 14-14S23.72 2 16 2zm0 25.45a11.4 11.4 0 0 1-5.82-1.59l-.42-.25-4.37 1.05 1.07-4.26-.27-.44A11.4 11.4 0 1 1 16 27.45zm6.28-8.57c-.34-.17-2.03-1-2.35-1.12-.31-.11-.54-.17-.77.18-.23.34-.89 1.11-1.09 1.34-.2.23-.4.26-.74.09-.34-.17-1.45-.54-2.77-1.71-1.02-.91-1.71-2.04-1.91-2.38-.2-.35-.02-.53.15-.7.16-.16.34-.4.51-.6.17-.2.23-.35.35-.58.11-.23.06-.43-.03-.6-.09-.17-.77-1.86-1.06-2.55-.28-.67-.56-.58-.77-.59h-.66c-.23 0-.6.09-.92.43-.31.34-1.2 1.18-1.2 2.87s1.23 3.33 1.4 3.56c.17.23 2.42 3.7 5.87 5.19.82.35 1.46.57 1.96.72.82.26 1.57.23 2.17.14.66-.1 2.03-.83 2.32-1.63.29-.8.29-1.49.2-1.63-.08-.14-.31-.23-.65-.4z"
              />
            </svg>
            {{ amMessageLabel }}
          </button>
          <button
            type="button"
            class="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-black-10 bg-white px-4 text-[13.5px] font-semibold text-black-100 transition-colors hover:bg-black-03"
            @click="onBookMeeting"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="text-black-80"
              aria-hidden="true"
            >
              <rect
                x="3"
                y="4"
                width="18"
                height="18"
                rx="2"
              />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            {{ t('billing.curiosityLock.bookMeeting') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.cl {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  min-height: 360px;
  border-radius: 1rem;
  border: 1px solid rgba(255, 182, 72, 0.28);
  background: radial-gradient(120% 80% at 50% -10%, rgba(255, 246, 231, 0.9) 0%, #ffffff 60%);
  animation: cl-pop 0.5s cubic-bezier(0.2, 0.7, 0.2, 1) both;
}

/* ── Frosted product teaser ─────────────────────────────────────────────── */
.cl-glass {
  position: absolute;
  inset: 0;
  pointer-events: none;
  user-select: none;
  overflow: hidden;
}

.cl-glass-inner {
  height: 100%;
  padding: 26px 30px;
  filter: blur(7px);
  opacity: 0.5;
  transform: scale(1.05);
  animation: cl-drift 9s ease-in-out infinite;
}

/* Decorative dashboard-ish mock */
.cl-mock {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
}

.cl-mock-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cl-mock-avatar {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: rgba(212, 160, 50, 0.35);
  flex-shrink: 0;
}

.cl-mock-bar {
  height: 12px;
  border-radius: 999px;
  background: rgba(13, 15, 21, 0.1);
}

.cl-mock-chip {
  margin-inline-start: auto;
  width: 74px;
  height: 26px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(255, 182, 72, 0.55), rgba(255, 182, 72, 0.3));
}

.cl-mock-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.cl-mock-stat {
  height: 58px;
  border-radius: 12px;
  background: rgba(13, 15, 21, 0.05);
  border: 1px solid rgba(13, 15, 21, 0.05);
}

.cl-mock-chart {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 96px;
  padding: 10px 2px 0;
  border-radius: 12px;
  background: rgba(255, 246, 231, 0.5);
}

.cl-mock-cbar {
  flex: 1;
  border-radius: 6px 6px 0 0;
  background: linear-gradient(180deg, rgba(255, 182, 72, 0.85), rgba(255, 182, 72, 0.4));
  animation: cl-breathe 3.2s ease-in-out infinite;
}

.cl-mock-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cl-mock-row {
  height: 11px;
  border-radius: 999px;
  background: rgba(13, 15, 21, 0.08);
}

/* ── Scrim ──────────────────────────────────────────────────────────────── */
.cl-scrim {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.62) 0%, rgba(255, 255, 255, 0.9) 44%, #ffffff 100%);
}

/* ── Lock card ──────────────────────────────────────────────────────────── */
.cl-card {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 44px 24px;
  text-align: center;

  @media (min-width: 744px) {
    padding: 56px 40px;
  }

  > * {
    opacity: 0;
    animation: cl-rise 0.55s cubic-bezier(0.2, 0.7, 0.2, 1) forwards;
  }

  > *:nth-child(1) {
    animation-delay: 0.06s;
  }
  > *:nth-child(2) {
    animation-delay: 0.12s;
  }
  > *:nth-child(3) {
    animation-delay: 0.18s;
  }
  > *:nth-child(4) {
    animation-delay: 0.24s;
  }
  > *:nth-child(5) {
    animation-delay: 0.3s;
  }
}

/* ── Primary CTA shimmer ────────────────────────────────────────────────── */
.cl-cta-primary {
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(100deg, transparent 30%, rgba(255, 255, 255, 0.5) 50%, transparent 70%);
    transform: translateX(-160%);
    animation: cl-sweep 3.6s ease-in-out infinite;
    pointer-events: none;
  }

  // RTL: the shimmer sweeps from the inline-start (right) edge.
  [dir='rtl'] &::after {
    transform: translateX(160%);
    animation-name: cl-sweep-rtl;
  }

  .cl-cta-label,
  .cl-cta-arrow {
    position: relative;
    z-index: 1;
  }

  .cl-cta-arrow {
    transition: transform 0.2s ease;
  }

  &:hover .cl-cta-arrow {
    transform: translateX(3px);
  }

  // RTL: the → glyph is mirrored to point toward the inline end; the hover nudge follows it.
  [dir='rtl'] & .cl-cta-arrow {
    transform: scaleX(-1);
  }

  [dir='rtl'] &:hover .cl-cta-arrow {
    transform: translateX(-3px) scaleX(-1);
  }
}

/* ── Keyframes ──────────────────────────────────────────────────────────── */
@keyframes cl-pop {
  from {
    opacity: 0;
    transform: scale(0.985) translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes cl-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes cl-sweep {
  0% {
    transform: translateX(-160%);
  }
  35% {
    transform: translateX(160%);
  }
  100% {
    transform: translateX(160%);
  }
}

@keyframes cl-sweep-rtl {
  0% {
    transform: translateX(160%);
  }
  35% {
    transform: translateX(-160%);
  }
  100% {
    transform: translateX(-160%);
  }
}

@keyframes cl-breathe {
  0%,
  100% {
    transform: scaleY(0.82);
    transform-origin: bottom;
  }
  50% {
    transform: scaleY(1);
    transform-origin: bottom;
  }
}

@keyframes cl-drift {
  0%,
  100% {
    transform: scale(1.05) translateY(0);
  }
  50% {
    transform: scale(1.05) translateY(-8px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .cl,
  .cl-card > *,
  .cl-glass-inner,
  .cl-mock-cbar,
  .cl-cta-primary::after {
    animation: none !important;
  }

  .cl-card > * {
    opacity: 1;
  }
}
</style>
